<!-- Computer sidebar: logo (Home), sections with their sub-pages (Plan, Tools, Collections), then at the bottom the
     PayPal donation button, a divider, Search, How it works, Settings and the button that reduces it to its icons (the
     names become tooltips; device preference `navCollapsed`, settings store).
     Hidden on the phone (MobileBar + MobileMenu), so its classes are the computer's only. The ids are used by the guided tour (tour.js). -->
<script setup lang="ts">
import { computed } from 'vue'
import HideoLogo from './HideoLogo.vue'
import DonateButton from './DonateButton.vue'
import { MAIN_SECTIONS, SUB_TABS } from '../navigation'
import { usePage } from '../composables/usePage'
import { useI18n } from '../composables/useI18n'
import { useDialogsStore } from '../stores/dialogs'
import { useSettingsStore } from '../stores/settings'

const { page, section, goTo } = usePage()
const { t } = useI18n()
const dialogs = useDialogsStore()
const settings = useSettingsStore()
const small = computed(() => !!settings.values.navCollapsed)
const toggle = () => settings.set('navCollapsed', !small.value)
const mainCls = 'flex-none w-full flex items-center space-x-3 px-4 py-2.5 hover:text-gray-100 hover:bg-darkbtn rounded-xl font-medium transition-colors'
const mainSmallCls = 'flex w-full items-center justify-center py-2.5 hover:text-gray-100 hover:bg-darkbtn rounded-xl transition-colors'
const subCls = 'flex w-full items-center gap-2.5 pl-11 pr-4 py-1.5 text-[13px] hover:text-gray-100 hover:bg-darkbtn rounded-lg font-medium transition-colors'
const subSmallCls = 'flex w-full items-center justify-center py-1.5 text-[13px] hover:text-gray-100 hover:bg-darkbtn rounded-lg transition-colors'
const bottomCls = 'flex w-full items-center space-x-3 px-4 py-2.5 hover:text-gray-100 hover:bg-darkbtn rounded-xl font-medium transition-colors'
const bottomSmallCls = 'flex w-full items-center justify-center py-2.5 hover:text-gray-100 hover:bg-darkbtn rounded-xl transition-colors'
const tone = (on: boolean) => (on ? 'text-brand' : 'text-gray-500')
/** The name as a tooltip, only when the sidebar is reduced */
const tip = (label: string) => (small.value ? label : undefined)
</script>

<template>
  <nav id="mainNav" :class="['hidden md:flex flex-col bg-darkcard border-r border-gray-800 justify-start md:order-1 flex-shrink-0 z-40 overflow-y-auto overflow-x-hidden transition-[width] duration-200', small ? 'w-[4.5rem]' : 'w-64']">
    <div :class="[small ? 'py-6 px-3' : 'p-6']">
      <h1 :title="t('Home')" :class="['cursor-pointer text-xl font-black tracking-wider text-brand flex items-center gap-2.5', { 'justify-center': small }]" @click="goTo('home')">
        <HideoLogo class="w-8 h-8 flex-shrink-0" />
        <span v-if="!small" class="flex flex-col leading-tight">
          <span class="text-gray-100">HIDEO</span>
          <span class="text-[11px] font-semibold tracking-normal text-gray-500">{{ t('Your content plan, in a moment') }}</span>
        </span>
      </h1>
    </div>

    <div :class="['flex flex-col w-full space-y-1', small ? 'px-3' : 'px-4']">
      <template v-for="[sec, label, icon, first] in MAIN_SECTIONS" :key="sec">
        <button :id="'nav-' + sec" :title="tip(label)" :class="[small ? mainSmallCls : mainCls, tone(section === sec)]" @click="goTo(first)">
          <i :class="['fas', icon, 'text-base w-5 text-center']"></i>
          <span v-if="!small" class="text-sm font-semibold">{{ label }}</span>
        </button>
        <button v-for="[sub, subLabel, subIcon] in SUB_TABS[sec] || []" :id="'nav-sub-' + sub" :key="sub" :title="tip(subLabel)" :class="[small ? subSmallCls : subCls, tone(page === sub)]" @click="goTo(sub)">
          <i :class="['fas', subIcon, 'text-xs w-4 text-center']"></i>
          <span v-if="!small">{{ subLabel }}</span>
        </button>
      </template>
    </div>
    <div :class="['mt-auto pb-2', small ? 'px-3' : 'px-4']"><DonateButton :compact="small" /></div>
    <div :class="['pt-4 space-y-1 border-t border-gray-800', small ? 'px-3' : 'px-4']">
      <button :title="small ? t('Search') + ' (Ctrl+K)' : undefined" :class="[small ? bottomSmallCls : bottomCls, 'text-gray-500']" @click="dialogs.show('search', {})">
        <i class="fas fa-magnifying-glass w-5 text-center"></i>
        <template v-if="!small">
          <span class="text-sm font-semibold">{{ t('Search') }}</span>
          <span class="ml-auto text-[10px] text-gray-600">Ctrl+K</span>
        </template>
      </button>
      <button id="nav-help" :title="tip(t('How it works'))" :class="[small ? bottomSmallCls : bottomCls, tone(page === 'help')]" @click="goTo('help')">
        <i class="fas fa-book-open w-5 text-center"></i>
        <span v-if="!small" class="text-sm font-semibold">{{ t('How it works') }}</span>
      </button>
      <button id="nav-settings" :title="tip(t('Settings'))" :class="[small ? bottomSmallCls : bottomCls, tone(page === 'settings')]" @click="goTo('settings')">
        <i class="fas fa-gear w-5 text-center"></i>
        <span v-if="!small" class="text-sm font-semibold">{{ t('Settings') }}</span>
      </button>
      <button id="navCollapseBtn" :title="small ? t('Expand the menu') : undefined" :class="[small ? bottomSmallCls : bottomCls, 'text-gray-500']" @click="toggle">
        <i :class="['fas', small ? 'fa-angles-right' : 'fa-angles-left', 'w-5 text-center']"></i>
        <span v-if="!small" class="text-sm font-semibold">{{ t('Collapse the menu') }}</span>
      </button>
    </div>
    <div class="h-4"></div>
  </nav>
</template>
